import { useCallback } from "react";
import {
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuIcon,
} from "@/components/ui/dropdown-menu";
import { MultiFilterMenuItemType } from "../FilterMenu.model";
import { isNullFilter } from "../FieldFilter.model";
import { MultiFilterMenuOptions } from "./MultiFilterMenuOptions";

interface MultiFilterMenuItemProps<Field extends string>
  extends MultiFilterMenuItemType<Field> {
  onChange: (item: MultiFilterMenuItemType<Field>) => void;
}

export function MultiFilterMenuItem<Field extends string>({
  keepOpenOnSelect = false,
  searchable = false,
  onChange,
  ...item
}: MultiFilterMenuItemProps<Field>) {
  const handleOptionClick = useCallback(
    (option: string) => {
      if (item.value == null || isNullFilter(item.value)) {
        onChange({
          ...item,
          value: {
            operator: "in",
            value: [option],
          },
        });

        return;
      }

      if (keepOpenOnSelect) {
        const nextValue = item.value.value.includes(option)
          ? item.value.value.filter((value) => value !== option)
          : [...item.value.value, option];

        if (nextValue.length === 0) {
          onChange({ ...item, value: undefined });
          return;
        }

        onChange({
          ...item,
          value: {
            operator: item.value.operator,
            value: nextValue,
          },
        });

        return;
      }

      onChange({
        ...item,
        value: {
          operator: item.value?.operator ?? "in",
          value: [...(item.value?.value || []), option],
        },
      });
    },
    [item, onChange]
  );

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger>
        {item.icon && <DropdownMenuIcon>{item.icon}</DropdownMenuIcon>}
        {item.label}
      </DropdownMenuSubTrigger>
      <DropdownMenuSubContent sideOffset={8} alignOffset={-5}>
        <MultiFilterMenuOptions
          label={item.label}
          options={item.options}
          selected={item.value?.value}
          searchable={searchable}
          keepOpenOnSelect={keepOpenOnSelect}
          onOptionClick={handleOptionClick}
        />
      </DropdownMenuSubContent>
    </DropdownMenuSub>
  );
}
